import moment from 'jalali-moment';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useRefresh } from '@/hooks';
import { ReserveHttp } from '@/services';
import { useUserContext } from '../UserContext';

export interface CurrentReservesContextType {
  loading: boolean;
  error: string;
  refresh: () => void;
  items: any[];
  fetchTodayReserves: (setLoadingAfterFinish?: boolean) => Promise<void>;
  fetchTodayDailySalesReserves: (setLoadingAfterFinish?: boolean) => Promise<void>;
}

export const CurrentReservesContext = createContext<CurrentReservesContextType>({
  loading: false,
  error: null,
  refresh: () => {},
  items: [],
  fetchTodayReserves: async () => { },
  fetchTodayDailySalesReserves: async () => { }
});


export const CurrentReservesContextProvider = ({ children }) => {


  const { isAuth } = useUserContext();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshCounter, refresh] = useRefresh();
  const [items, setItems] = useState([]);
  const reservesItemsRef = useRef<any>([]);
  const reservesDailySalesItemsref = useRef<any>([]);

  const fetchTodayReserves = useCallback(async (setLoadingAfterFinish = true) => {
    setLoading(true);
    const today = moment().format('jYYYY/jMM/jDD');
    return ReserveHttp.fetchCurrentReserves()
      .then((response) => {
        const payload = response?.data?.payload;
        // // add mealTypes array if not exists
        // const mealTypesDates = payload?.mealTypes?.map(mealType => ({
        //   mealStartDate: mealType.startServeTime,
        //   MealendDate: mealType.endServeTime,
        // }));

        const weekDays = payload?.weekDays?.map(item => ({ ...item, mealTypes: item.mealTypes || [] })) ?? [];
        const todayList = weekDays.find(day => day.dateJStr === today);

        reservesItemsRef.current = [...todayList.mealTypes];
        setItems([...reservesItemsRef.current, ...reservesDailySalesItemsref.current]);

      })
      .catch(e => {
        console.error(e);
        setError(e);
      })
      .finally(() => {
        if (setLoadingAfterFinish) setLoading(false);
      });
  }, [reservesItemsRef, reservesDailySalesItemsref]);

  const fetchTodayDailySalesReserves = useCallback(async (setLoadingAfterFinish = true) => {
    setLoading(true);
    return ReserveHttp.fetchCurrentDailySaleReserves()
      .then((response) => {
        reservesDailySalesItemsref.current = [];
        const dailySellReserves = response?.data?.payload;
        if (!dailySellReserves || !Array.isArray(dailySellReserves)) return;
        for (const reserve of dailySellReserves as DailySaleProgramEntity[]) {
          // eslint-disable-next-line no-unsafe-optional-chaining
          const [food, besideFood] = reserve?.foodNames?.split('|');
          if (reserve.selectedCount > 0) {
            reservesDailySalesItemsref.current.push({
              mealTypeId: reserve.mealTypeId,
              name: reserve.mealTypeName,
              startServeTime: reserve.startServeTime,
              endServeTime: reserve.finishServeTime,
              demonstrationDescription: reserve.demonstrationDescription,
              reserve: {
                id: reserve.id,
                programId: reserve.programId,
                reserveDailySaleId: reserve.reserveDailySaleId,
                dailySellProgramId: reserve.dailySellProgramId,
                foodNames: food,
                isDailyReserve: true,
                selfId: reserve.selfCode,
                besideFoodNames: besideFood || '',
                remainedCount: reserve.remainCount,
                foodTypeTitle: reserve.foodTypeTitle,
                selectedCount: reserve.selectedCount,
                selfCodeName: reserve.selfName,
                foodImageSrc: reserve.foodImageSrc,

              },
            });
          }
        }
        setItems([...reservesItemsRef.current, ...reservesDailySalesItemsref.current]);
      })
      .finally(() => {
        if (setLoadingAfterFinish) setLoading(false);
      });
  }, [reservesItemsRef, reservesDailySalesItemsref]);

  const fetchReserves = useCallback(async () => {
    if (!isAuth) return;
    setLoading(true);
    const taskFetchCurrentReservese = fetchTodayReserves(false);
    const taskFetchDailySalesProgram = fetchTodayDailySalesReserves(false);
    try {
      setItems([]);
      await Promise.all([taskFetchCurrentReservese, taskFetchDailySalesProgram]);
      // setItems([...reservesItemsRef.current, ...reservesDailySalesItemsref.current])
    }
    finally {
      setLoading(false);
    }
  }, [items, isAuth]);

  useEffect(() => {
    fetchReserves();
  }, [refreshCounter, isAuth]);

  return (
    <CurrentReservesContext.Provider value={{
      loading,
      error,
      refresh,
      items,
      fetchTodayReserves,
      fetchTodayDailySalesReserves,
    }}>
      {children}
    </CurrentReservesContext.Provider>
  );
};

export default CurrentReservesContextProvider;
